import type { ReactElement, ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { remarkCallouts } from './markdownCallouts';
import { MermaidDiagram } from './MermaidDiagram';

/**
 * Shared Markdown renderer. No rehype-raw: raw HTML in bodies stays inert
 * (help bodies are per-tenant overridable). remark-gfm adds tables/strikethrough/autolinks.
 *
 * Fenced code blocks (bare and language-tagged) are handled at the `pre` level: the
 * component extracts the text from its child <code> element and renders its own plain
 * <code> inside the dark `pre`, so the inline-pill `code` component is never invoked for
 * blocks — avoiding the light bg-slate-100 pill nested inside the dark bg-slate-900 pre.
 */
export function Markdown({ source }: { source: string }) {
  return (
    <div className="text-sm leading-relaxed text-slate-700">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkCallouts]}
        components={{
          h1: ({ children }) => <h1 className="mb-3 text-lg font-semibold text-slate-900">{children}</h1>,
          h2: ({ children }) => <h2 className="mb-2 mt-4 text-base font-semibold text-slate-900">{children}</h2>,
          h3: ({ children }) => <h3 className="mb-2 mt-3 text-sm font-semibold text-slate-900">{children}</h3>,
          p: ({ children }) => <p className="mb-3">{children}</p>,
          ul: ({ children }) => <ul className="mb-3 list-disc pl-5">{children}</ul>,
          ol: ({ children }) => <ol className="mb-3 list-decimal pl-5">{children}</ol>,
          li: ({ children }) => <li className="mb-1">{children}</li>,
          hr: () => <hr className="my-4 border-slate-200" />,
          strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          a: ({ children, href }) => (
            <a href={href} className="text-brand underline" target="_blank" rel="noreferrer">{children}</a>
          ),
          table: ({ children }) => (
            <div className="mb-3 overflow-x-auto">
              <table className="w-full border-collapse text-xs">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-slate-100">{children}</thead>,
          tr: ({ children }) => <tr className="border-b border-slate-200">{children}</tr>,
          th: ({ children }) => <th className="px-2 py-1 text-left font-semibold text-slate-900">{children}</th>,
          td: ({ children }) => <td className="px-2 py-1 align-top">{children}</td>,
          pre: ({ children }) => {
            // Extract the raw text from the child <code> React element so we can render
            // our own unstyled <code> inside the dark pre, bypassing the inline-pill
            // code component entirely (react-markdown only calls custom components for
            // AST nodes it processes — a <code> we write ourselves in JSX is plain HTML).
            const el = Array.isArray(children) ? children[0] : children;
            const codeEl = el as ReactElement<{ className?: string; children?: ReactNode }> | null;
            const text = codeEl?.props?.children ?? children;

            // Route mermaid fenced blocks to the diagram component.
            if ((codeEl?.props?.className ?? '').includes('language-mermaid')) {
              return <MermaidDiagram chart={String(text).replace(/\n$/, '')} />;
            }

            return (
              <pre className="mb-3 overflow-x-auto rounded bg-slate-900 p-3 text-xs text-slate-100">
                <code>{text}</code>
              </pre>
            );
          },
          // Only called for inline code (fenced blocks are intercepted by `pre` above).
          code: ({ children }) => (
            <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">{children}</code>
          ),
          blockquote: ({ node, children }) => {
            const cls = (node?.properties?.className as string[] | undefined) ?? [];
            const type = cls.find((c) => c.startsWith('callout-'))?.replace('callout-', '');
            if (!type) return <blockquote className="mb-3 border-l-4 border-slate-300 pl-3 italic text-slate-600">{children}</blockquote>;
            const variant: Record<string, string> = {
              note: 'border-blue-300 bg-blue-50',
              tip: 'border-green-300 bg-green-50',
              important: 'border-violet-300 bg-violet-50',
              warning: 'border-amber-300 bg-amber-50',
              insight: 'border-slate-300 bg-slate-50',
            };
            return (
              <div data-callout={type} className={`mb-3 rounded border-l-4 p-3 ${variant[type] ?? variant.note}`}>
                {children}
              </div>
            );
          },
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}
